import type { FloraCatalog } from '@/flora/FloraCatalog';
import { PAVING_MATERIALS, ROOF_SHAPES } from '@/site/SiteObject';
import { surfaceLayers } from '@/terrain/TerrainSurface';
import { MAX_REQUESTED_MATERIALS } from './SitePipeline';
import { WEATHER_PRESETS } from '@/environment/EnvironmentState';
import { frameBrief, type MapFrame } from '@/geo/MapFrame';
import type { ReferencePrompt } from './SitePlanClient';
import { referenceTagBrief, type ReferenceTag } from './ReferenceTag';
import {
  PIPELINE_STAGES,
  PIPELINE_STAGE_IDS,
  SEMANTIC_CATEGORIES,
  artifactForDisplay,
  stageMeta,
  type PipelineArtifacts,
  type PipelineStageId,
} from './SitePipeline';

/**
 * The five stage prompts.
 *
 * Each is `docs/SITE_FROM_REFERENCE.md`'s method cut at its natural seams, so a stage asks for one
 * kind of judgement and is given exactly the evidence that judgement needs — the photograph, plus
 * the artifacts of the stages before it, verbatim.
 *
 * Passing prior artifacts as **JSON rather than prose** is the load-bearing decision here. It means
 * a later stage is answering against the same numbers the compiler will use, so when the planting
 * lands in the driveway the cause is visible in the artifact rather than lost in a paraphrase. It is
 * also what makes re-running a single stage meaningful: stage 4 re-run against an edited stage 1
 * genuinely reconsiders, because its input actually changed.
 *
 * Pure and deterministic — the same inputs always produce the same text, and the inspector shows
 * each prompt before it is sent.
 */

export interface StagePromptOptions {
  stage: PipelineStageId;
  catalog: FloraCatalog;
  /** Artifacts from the stages already completed. */
  artifacts: Partial<PipelineArtifacts>;
  /** Free text typed by the user, appended verbatim as the highest-priority instruction. */
  direction?: string;
  /**
   * Semantic tags placed on the photograph before the run — the designer's, and any the pre-pass
   * suggested. Optional: a run with none is a legitimate run working from less.
   */
  tags?: readonly ReferenceTag[];
  /**
   * The map frame the reference was captured from, when it came from one.
   *
   * Its presence changes stage 1's job: the extent is measured rather than estimated, so the scale
   * hunt is replaced by an instruction not to perform it. Absent for an uploaded photograph, which
   * still has to be scaled the hard way.
   */
  frame?: MapFrame;
  /** How many images accompany the request, so the prompt can refer to them by position. */
  imageCount?: number;
}

export function buildStagePrompt(options: StagePromptOptions): ReferencePrompt {
  return {
    // Byte-identical for every stage of a run, and that is the point rather than a tidiness: the
    // provider discounts a matching *prefix*, so a stage name anywhere in here would end the match
    // before the artifacts below it were reached. Everything stage-specific is in the user message,
    // behind the evidence the neighbouring stages share. See `shared/ai/caching.ts`.
    system: [
      PIPELINE,
      FRAME,
      // Immediately after the frame section, because it contradicts it: `FRAME` teaches how to
      // correct an oblique photograph for perspective, and a map capture has none to correct. A
      // stage that applies the correction anyway will squash the site's depth by a third.
      options.frame ? frameBrief(options.frame) : '',
    ]
      .filter((section) => section.length > 0)
      .join('\n\n'),
    user: userMessage(options),
  };
}

/* ----------------------------------- Shared ----------------------------------- */

/**
 * The roster, generated from the stage list so it cannot drift from the pipeline it describes.
 *
 * It replaces the line each stage used to carry on its own — "later stages handle the work you are
 * not being asked for" — which named no stage, and so left every boundary to be restated in the
 * bodies ("belongs to stage 5 instead"). Saying once what all five own is shorter overall and a
 * better answer to the question a stage actually has, which is where its own work stops.
 */
const PIPELINE = `You are one stage of a five-stage landscape reconstruction pipeline. Every stage
reads the same photograph of a real place and returns one JSON object; a later stage is given the
output of the earlier ones verbatim, as its input.

${PIPELINE_STAGES.map((meta, index) => `${index + 1}. **${meta.title}** — ${meta.summary}`).join('\n')}

Which of those you are is stated further down, after the evidence. Do that stage and only that
stage: work you are not being asked for belongs to a stage that knows how to do it properly, and an
error you make here is not corrected later, it is inherited.

Return one JSON object and nothing else: no prose, no markdown fence, no commentary outside the
"notes" fields the schema provides for it.`;

function role(stage: PipelineStageId): string {
  const meta = stageMeta(stage);
  const index = 1 + PIPELINE_STAGE_IDS.indexOf(stage);
  return `# You are stage ${index} of 5: ${meta.title}

${meta.summary}`;
}

const FRAME = `# The coordinate frames

Two spaces, and mixing them is the single most damaging mistake available here.

- IMAGE SPACE — normalized [u, v], both in [0, 1], origin at the TOP-LEFT of the reference image,
  u increasing right, v increasing DOWN. Stage 1 works entirely in this space.
- WORLD SPACE — metres, origin at the CENTRE of the site, +X to image-right, +Z to image-DOWN,
  Y up. The site spans ±widthMeters/2 by ±lengthMeters/2. Stages 2 onward work entirely in this
  space.

The mapping between them, once stage 1 has fixed the site extent:
  x = (u - 0.5) * widthMeters
  z = (v - 0.5) * lengthMeters
This is exact only for a true overhead view. For an oblique or ground-level photograph, correct for
perspective: ground further from the camera is compressed in v, so a region occupying the top third
of an oblique frame covers far more than a third of the site's depth. Say in "notes" which you did.`;

const OUTPUT_DISCIPLINE = `# Before you answer

- Every number is finite and in range. No nulls, no strings where a number belongs.
- Every polygon is a simple closed ring listed in order, at least 3 vertices, NOT self-intersecting,
  and with the last vertex NOT repeating the first.
- Anything you inferred rather than observed goes in "notes", named as an inference. Unmarked
  invention is the only real failure mode in this pipeline — an approximation that says so is
  useful, one that does not is misleading.

Return only the JSON object.`;

/**
 * Ordered most stable to least, which here is also most shared to least.
 *
 * The evidence comes before the instruction, which is the reverse of the obvious arrangement and is
 * deliberate on two counts. It is what lets stages 4 and 5 — handed the same three artifacts —
 * share a prefix thousands of tokens long instead of diverging at the first word. And it makes the
 * prompt say what it always meant: the planting stage's rule about reading stage 1's plant labels
 * says "every vegetation region above", and until this order those regions were below it.
 */
function userMessage(options: StagePromptOptions): string {
  const parts: string[] = [];

  const priors = priorArtifacts(options);
  if (priors.length > 0) parts.push(priors);

  // After the priors so the world coordinates in the brief can be read against the extent stage 1
  // fixed, and before the direction so free text still has the last word.
  const brief = referenceTagBrief(
    options.tags ?? [],
    options.stage === 'segment' ? undefined : options.artifacts.segment?.site,
  );
  if (brief.length > 0) parts.push(brief);

  const count = options.imageCount ?? 1;
  const meta = stageMeta(options.stage);
  const images =
    count === 1
      ? 'The attached photograph is the reference.'
      : `The ${count} attached photographs are the reference; they show the same site.`;

  parts.push(
    role(options.stage),
    `${images} Carry out the ${meta.title.toLowerCase()} stage.`,
    body(options),
    OUTPUT_DISCIPLINE,
  );

  const direction = options.direction?.trim();
  if (direction) {
    // Placed last and named as an override so it wins any conflict with the generic method — it is
    // the one part of the prompt that came from someone who has seen the actual site.
    parts.push(
      'ADDITIONAL DIRECTION FROM THE DESIGNER — this comes from someone who knows this site, and ' +
        'overrides the general guidance above wherever the two conflict:\n' +
        direction,
    );
  }

  return parts.join('\n\n');
}

/**
 * Prior stage output, verbatim.
 *
 * Only the stages this one actually depends on: handing the structures stage the full planting list
 * would spend thousands of tokens on something it must not contradict but need not read.
 */
function priorArtifacts(options: StagePromptOptions): string {
  const { artifacts, stage } = options;
  const sections: string[] = [];

  if (stage !== 'segment' && artifacts.segment) {
    sections.push(
      `## Stage 1 — semantic map (authoritative: use these region ids, this scale, this site extent)\n\n${json(artifacts.segment)}`,
    );
  }
  if ((stage === 'surfaces' || stage === 'flora' || stage === 'structures') && artifacts.terrain) {
    sections.push(`## Stage 2 — terrain\n\n${json(artifacts.terrain)}`);
  }
  if ((stage === 'flora' || stage === 'structures') && artifacts.surfaces) {
    sections.push(`## Stage 3 — ground surfaces\n\n${json(artifacts.surfaces)}`);
  }
  if (stage === 'flora' && artifacts.structures) {
    // Only present when the flora stage is being re-run after a full pass; planting must dodge what
    // is already built.
    sections.push(`## Stage 5 — structures already placed\n\n${json(artifacts.structures)}`);
  }

  return sections.length > 0 ? `# What earlier stages established\n\n${sections.join('\n\n')}` : '';
}

/**
 * Bulk is elided on the way in — see {@link artifactForDisplay}. A measured terrain lattice is tens
 * of thousands of numbers that would cost more tokens than the rest of the run and tell a model
 * nothing it can act on; its control points, which stay, are the landform in the form a model reads.
 */
function json(value: unknown): string {
  return `\`\`\`json\n${JSON.stringify(artifactForDisplay(value), null, 1)}\n\`\`\``;
}

/* -------------------------------- Stage bodies -------------------------------- */

function body(options: StagePromptOptions): string {
  switch (options.stage) {
    case 'segment':
      return segmentBody();
    case 'terrain':
      return terrainBody();
    case 'surfaces':
      return surfacesBody();
    case 'flora':
      return floraBody(options.catalog);
    case 'structures':
      return structuresBody();
  }
}

function segmentBody(): string {
  return `# Your task

Identify everything in the photograph, as labelled polygons in IMAGE space, and fix the scale.

1. SCALE FIRST, and state it. Find one object of known real size in frame — a car (~4.5 m long), a
   door (~0.9 m wide, ~2.0 m tall), a single garage bay (~3 m), a patio table (~1.2 m), a bicycle
   (~1.7 m), a tennis court (23.77 x 10.97 m). Record what you used, the fraction of the IMAGE WIDTH
   it spans, and its real size. Everything downstream is a multiple of this number, and it is the
   step most often skipped.

2. SIZE THE SITE from that scale. Include the visible ground PLUS the boundary planting that
   encloses it — a site cropped to the lawn loses what makes it a place.

3. SEGMENT. One region per distinguishable thing. Trace its outline as a polygon in normalized image
   coordinates. Be generous with region count: 15-40 regions for a typical residential photograph is
   right, one region for "the garden" is not. Separate regions that will be treated differently:
   the driveway is not the walkway, the lawn is not the bed, each tree canopy is its own region.

   Categories: ${SEMANTIC_CATEGORIES.join(' | ')}
   - "building"      roofs and walls of structures
   - "paving"        driveways, walks, patios, decks — anything hard and walkable
   - "water"         pools, ponds, streams
   - "lawn"          mown turf
   - "planting-bed"  mulched or planted beds, borders
   - "tree"          an individual canopy, or a contiguous mass of canopy
   - "shrub"         shrub masses and hedges
   - "bare-earth"    soil, gravel that is not a drive, worn ground
   - "vehicle"       cars, trailers — identified so later stages know to ignore them
   - "unmodelled"    fences, walls, furniture, people, pots, lighting

4. NAME THE PLANTS, as far as the photograph honestly supports it. A vegetation region's "label" is
   the only botany this pipeline ever records: stage 4 has a fixed catalog and must substitute into
   it, and it can only say what a substitution cost if you tell it what was actually there.

   Give the binomial where the plant is genuinely identifiable — "Acer saccharum, mature, ~12 m,
   broad oval crown". Where it is not, describe the plant in the terms a substitution is made on
   rather than guessing at a name: type and leaf ("broadleaf deciduous", "fine-needled conifer"),
   habit ("upright oval", "weeping", "multi-stemmed"), height and spread in metres, foliage colour,
   and whether it is clipped. "Deciduous shrub mass, ~1.2 m, arching, small opposite leaves" is
   worth far more than a confident wrong species.

   You have NOT been given the catalog, deliberately. Do not round to what you imagine is in it —
   naming the real plant and substituting later is the whole reason those are two stages.

5. READ THE LIGHT, THEN SET THE CLOCK. Shadow direction gives the sun's bearing; the ratio of a
   shadow's length to its caster's height gives its elevation. Record both, then give the hour,
   dayOfYear and latitude that reproduce them. The renderer's sun model treats world +Z as compass
   north and IGNORES any north setting, so the site is oriented by choosing that time, not by a
   field. A low sun in winter at temperate latitudes is correct, not an error.

6. READ THE SEASON FROM THE PLANTS, not from a guess about when the photo was taken. Bare branches
   over ochre ground is dormant (dayOfYear ~330-350). Full dark canopy over green mown turf is high
   summer (~170-200). Autumn colour is ~280-300. Give a specific day.

# Output schema

{
  "scale": { "description": "silver sedan, side-on, left of drive", "imageFraction": 0.18, "meters": 4.5 },
  "site": { "widthMeters": n, "lengthMeters": n, "orientation": "house faces roughly south-west" },
  "observations": {
    "dayOfYear": 1-365, "hour": 0-23, "minute": 0-59, "latitudeDegrees": n,
    "weather": ${WEATHER_PRESETS.map((w) => `"${w}"`).join(' | ')},
    "sunAzimuthDegrees": n, "sunElevationDegrees": n
  },
  "regions": [
    {
      "id": "region-driveway",
      "label": "asphalt driveway, cracked",
      "category": "paving",
      "polygon": [[u, v], [u, v], [u, v]],
      "confidence": 0-1,
      "notes": "continues past the frame edge"
    },
    {
      "id": "region-street-maple",
      "label": "Acer saccharum, mature, ~12 m, broad oval crown, full autumn colour",
      "category": "tree",
      "polygon": [[u, v], [u, v], [u, v]],
      "confidence": 0.6,
      "notes": "leaf shape reads clearly at this resolution; could be A. rubrum"
    }
  ],
  "unmodelled": ["picket fence along the street", "two cars", "porch furniture"],
  "notes": "what you inferred rather than saw"
}`;
}

function terrainBody(): string {
  return `# Your task

Read the ground's SHAPE — not its materials, not what stands on it — as elevation control points in
WORLD metres.

A compiler blends your control points into procedural noise with inverse-distance weighting and
bakes the result as the site's heightfield. So you are describing a landform, not a mesh: half a
dozen well-chosen readings beat fifty.

1. Establish the datum. Elevation 0 is the site's typical ground level. Everything is relative to it.

2. Find the real relief. Look at: the number of steps at a door (each ~0.17 m), retaining walls,
   how a driveway meets a street, where water would run, foreshortening of a lawn, the waterline of
   a pool. State in "notes" what you read the grade from.

3. Place control points where the ground actually does something — the top and toe of a slope, the
   low point of a swale, a terrace edge, the pad a building sits on. A point's "radiusMeters" is how
   far its influence reaches: 3-6 m for a mound, 15-30 m for a whole-site slope.

4. Be conservative. Most residential sites are nearly flat, and inventing relief is worse than
   missing it: a plant sitting on a phantom berm reads immediately as wrong. If the site is flat,
   say so and return few or no control points with a small "elevationAmplitude".

# Output schema

{
  "elevationAmplitude": 0-20,
  "roughness": 0-1,
  "controlPoints": [
    { "x": n, "z": n, "elevationMeters": n, "radiusMeters": n, "notes": "toe of the bank behind the shed" }
  ],
  "notes": "read the grade from the three steps at the back door and the retaining wall on the east"
}`;
}

function surfacesBody(): string {
  const layers = surfaceLayers().map(
    (layer) => `  - "${layer.id}" — ${layer.name}. ${layer.hint}${layer.plantable ? '' : ' Refuses planting.'}`,
  ).join('\n');

  return `# Your task

Convert the ground regions stage 1 found into PAINTED GROUND, as polygons in WORLD metres.

Everything that is a *surface treatment* belongs here; anything that is a raised built object — a
deck, a stone terrace with a step, a pool with a coping — belongs to stage 5 instead, and you should
leave it to that stage.

Materials already on the palette:
${layers}

## Asking for a material that is not there

If a ground surface in the photograph is not one of the above, do NOT round it to the nearest one.
Declare it in "materials" and paint with it. Rounding is the failure this exists to prevent: red
decomposed granite, bluestone, cocoa-shell mulch and pea shingle are four different surfaces, and
calling all four "gravel" bakes the error into the site before anyone sees it.

Each declaration is researched against the web and rendered as a real swatch, so:

- "request" should read like a materials specification, not a colour — "crushed red lava rock,
  20 mm chips, matte" beats "red gravel". Name the trade term if you can see one.
- "context" is where it goes on this site, which sharpens the research: a gravel drive and a gravel
  mulch in a dry garden are graded differently.
- Declare at most ${MAX_REQUESTED_MATERIALS}, and only for surfaces that cover enough ground to be
  worth it. A 1 m strip of a fifth paving type is better painted with an existing material and
  mentioned in "notes" — every declaration costs a search and a render.
- Use the palette where the palette is right. A tarmac drive is "blacktop"; do not declare a
  material for it because the photograph's asphalt is slightly grey.

Rules:

1. Map, do not invent. Every region you paint should come from a stage 1 region — put its id in
   "regionId". A surface with no corresponding region needs a reason in "notes".

2. Convert image coordinates to world metres using the frame above and stage 1's site extent. This
   is the step where an oblique photograph will bite: check that a driveway you paint is still a
   plausible 3-6 m wide in metres once converted.

3. Grass is the base layer and is already everywhere — you do NOT need to paint a lawn. Paint grass
   only to cut a hole in something else you painted.

4. Feather honestly. A driveway edge is crisp: 0.05-0.2 m. A mulch bed meeting a lawn is soft:
   0.3-0.8 m. A pond margin is softer still. Feathering is what stops the site reading as vinyl.

5. Water here means an open water SURFACE at ground level — a pond, a stream. A swimming pool with a
   built edge is stage 5's job; leave it.

# Output schema

{
  "materials": [
    {
      "id": "red-granite-path",
      "name": "Red Decomposed Granite",
      "request": "decomposed red granite path fines, 6 mm minus, compacted",
      "context": "the informal path running from the drive to the back gate"
    }
  ],
  "regions": [
    {
      "id": "surface-drive",
      "regionId": "region-driveway",
      "material": "blacktop",
      "polygon": [[x, z], [x, z], [x, z]],
      "featherMeters": 0.1,
      "notes": ""
    },
    {
      "id": "surface-path",
      "regionId": "region-path",
      "material": "red-granite-path",
      "polygon": [[x, z], [x, z], [x, z]],
      "featherMeters": 0.15,
      "notes": "reads as a warm red-brown fines path, not gravel"
    }
  ],
  "notes": "the drive runs off the south edge of the site; cut it at the boundary"
}

Omit "materials" entirely when the palette already covers the site.`;
}

function floraBody(catalog: FloraCatalog): string {
  const lines = catalog.list().map((definition) => {
    const growth = definition.growth;
    const common = definition.commonNames[0] ?? definition.botanicalName;
    const cultivar = definition.cultivar !== undefined ? ` '${definition.cultivar}'` : '';
    const evergreen = definition.seasons.evergreen ? 'evergreen' : 'deciduous';
    return (
      `  ${definition.id}\n` +
      `    ${definition.botanicalName}${cultivar} (${common}) — ${definition.category}, ${evergreen}\n` +
      `    mature ${growth.matureHeightMeters.typical} m tall x ${growth.matureSpreadMeters.typical} m wide;` +
      ` habit ${definition.rendering.geometryForm};` +
      ` recommended spacing ${definition.placement.recommendedSpacingMeters} m;` +
      ` sun ${definition.sunlight.join('/')}; moisture ${definition.moisture.join('/')}`
    );
  });

  return `# Your task

Turn the vegetation stage 1 found into planting MASSES, in WORLD metres.

You do NOT place individual plants. You describe where a mass goes and how many are in it; a
deterministic compiler expands that, sampling the terrain, honouring spacing, and keeping every
plant clear of what is built. Those are things you cannot do correctly from a photograph, so do not
try.

# The plant catalog — the ONLY species available

Use these ids exactly. Any other value is dropped on import and the mass is lost.

${lines.join('\n')}

# Rules

1. DECOMPOSE BY LAYER, not by individual. Canopy, understory, shrub, hedge, herbaceous,
   groundcover. Name each mass for what it is ("west boundary screen", "foundation planting",
   "front lawn specimen maple"). Give every mass a "group" so the user can select it as one thing.

2. SUBSTITUTE HONESTLY, AND FROM STAGE 1'S IDENTIFICATION. Every vegetation region above carries a
   label naming the real plant, or describing it in the terms a substitution is made on. That label
   is your input; do not re-identify the photograph, and do not discard it in favour of what looks
   convenient. Match on genus first, then on silhouette, mature size and seasonal behaviour.

   The catalog is small; a real site has dozens of species, so most masses WILL be substitutions.
   Say in "notes" what each one cost — "Acer saccharum read at the street; substituted A. rubrum,
   same habit and scale, autumn colour runs red rather than orange". A substitution that is recorded
   is a decision someone can overrule; one that is silent is indistinguishable from an observation.
   Never pick a species whose own record contradicts the site just because its shape fits — the
   plant data is real, and a plausible-looking site built on false botany is worse than an obviously
   approximate one.

3. NEVER CARPET A LAWN. Mown turf is the ground surface stage 3 painted, not a planting. Ornamental
   grasses belong at the rough margin where mowing stops.

4. DISTRIBUTE LIKE NATURE. Use "drifts" with gaps between them rather than one even wash over a
   region — uniform density is the most recognisable signature of a generated site. Use "line" for
   anything genuinely linear: a hedge, a screen, an avenue. Vary "maturity" across every mass; a
   real planting is every age at once. Set "linked": true only where the mass really is one repeated
   clipped plant, since linked members share size and colour exactly.

5. RESPECT WHAT IS PAINTED AND WHAT WILL BE BUILT. Do not place a mass over blacktop or water. Keep
   masses off the building footprints stage 1 identified — the compiler enforces this, and a mass
   that lands on a house simply loses its plants.

# Output schema

{
  "plantings": [
    {
      "id": "unique-id", "name": "West boundary screen", "speciesId": "thuja-occidentalis",
      "layer": "conifer", "minSpacingMeters": 1.2, "maturity": [0.55, 0.9],
      "group": "West boundary", "linked": true, "pruningStyle": "hedged",
      // EXACTLY ONE of the three placement forms:
      "regions": [{ "x0": n, "x1": n, "z0": n, "z1": n }], "count": n,
      "drifts": [{ "x": n, "z": n, "radiusMeters": n, "count": n }],
      "line": { "points": [[x, z], [x, z]], "spacingMeters": n, "offsetMeters": n, "jitterMeters": n }
    }
  ],
  "notes": "substitutions and what is wrong with each"
}

- "layer" groups plants that compete for room. Same layer = spacing enforced; different layers
  ignore each other, so shrubs can sit under trees. Use canopy / conifer / understory / shrub /
  hedge / grass / perennial / groundcover.
- "minSpacingMeters" may go below a species' recommended spacing where that is real: a woodland
  canopy interlocks at ~0.7x, a clipped hedge goes tighter still. A hedge at full nursery spacing is
  a row of lollipops.`;
}

function structuresBody(): string {
  return `# Your task

Build what is BUILT — houses, garages, sheds, decks, terraces, pools — as polygons in WORLD metres.

This tool models a building as extruded MASSES: each mass is a closed outline, a wall height to the
eaves, and a roof. That is what lets a real house be built instead of approximated by a box.

1. OUTLINE, DO NOT FIT A RECTANGLE. Trace the actual footprint as a polygon. An L-shaped house is an
   L-shaped polygon. A bowed bay stays bowed. You are not being asked to choose a "dominant
   rectangle" — that instruction belonged to an older version of this tool and produced garages
   sticking through lawns.

2. SPLIT A BUILDING INTO MASSES AT EVERY ROOF CHANGE. This is the rule that matters most. A roof is
   solved per mass, and one ridge cannot honestly span an L — it would roof the empty notch too. So:
   the main range is one mass, the cross-wing is another, the attached garage is another, the porch
   is another. Give each its own outline, eave height and roof. Masses should ABUT, not overlap.

   A mass whose outline is not close to rectangular gets a FLAT roof regardless of what you ask for,
   because there is no honest ridge to give it. If you wanted a pitch there, you needed more masses.

3. HEIGHTS FROM THE SCALE ANCHOR. A storey is ~2.7-3.0 m to the eaves. A single-storey ranch eave is
   ~2.4-2.7 m, two storeys ~5.4-6.0 m. A roof rise is ~0.3-0.5 x the mass's short span for a normal
   pitch. A garage eave is usually lower than the house it adjoins — that difference is exactly what
   massing exists to express.

4. ROOF FORM AND COLOUR from the photograph: ${ROOF_SHAPES.join(' | ')}. Sample the wall and roof
   colours off the image rather than reaching for a default. "ridgeCrosswise": true turns a ridge
   across a mass's long axis, for a cross-gabled wing.

5. FLAT BUILT SURFACES are kind "paving" with a polygon and a material:
   ${PAVING_MATERIALS.join(' | ')}. A swimming pool is paving with "water" — it occupies a footprint,
   refuses planting, and needs naming. Use "sitsOn" when one nests inside another (a pool inside its
   terrace), so it lands on the terrace's datum instead of the ground's.

6. CLEARANCE. Give each building the metre or so of access a person needs ("clearanceMeters": ~0.8).
   Paving usually takes 0, because a bed may legitimately run right to its edge.

# Output schema

{
  "structures": [
    {
      "id": "site-house", "kind": "building", "name": "House", "regionId": "region-house",
      "masses": [
        {
          "name": "main range",
          "polygon": [[x, z], [x, z], [x, z], [x, z]],
          "heightMeters": 5.6, "roof": "gable", "roofRiseMeters": 2.2,
          "baseMeters": 0, "ridgeCrosswise": false
        },
        {
          "name": "attached garage",
          "polygon": [[x, z], [x, z], [x, z], [x, z]],
          "heightMeters": 2.6, "roof": "gable", "roofRiseMeters": 1.1
        }
      ],
      "wallColor": "#rrggbb", "roofColor": "#rrggbb", "clearanceMeters": 0.8,
      "notes": "two storeys; the garage roof is lower and runs crosswise"
    },
    {
      "id": "site-pool", "kind": "paving", "name": "Pool", "regionId": "region-pool",
      "polygon": [[x, z], [x, z], [x, z], [x, z]],
      "paving": "water", "clearanceMeters": 0, "sitsOn": "site-terrace"
    }
  ],
  "notes": "what you could not model"
}

- A "paving" object uses a single "polygon"; only a "building" uses "masses".
- If a building genuinely is one simple box, one mass with a 4-vertex polygon is the right answer.
- No structure may overhang the site edge, and two buildings may not overlap.`;
}
